/*!
 * Cart Drawer Variant — JS controller
 *
 * The site renders BOTH the control and variant cart drawer (the variant is
 * default-hidden via CSS; Intelligems flips visibility for the variant
 * cohort). This file does two jobs:
 *
 *   1. PHASE 0 — Drawer-open routing shim. Add-to-cart handlers around the
 *      site call `.open()` on the first <cart-drawer> they find. When the
 *      variant is the active layout, that "first one" is the now-hidden
 *      control, and opening it would do nothing visible. The shim wraps
 *      every <cart-drawer>'s `.open()` so calls to a hidden drawer are
 *      forwarded to whichever drawer is currently visible.
 *
 *   2. PHASES 1+ — Cart actions for the variant (qty stepper, remove,
 *      Upgrade-and-Save subscription swap, upsell add-to-cart, popular-
 *      product add-to-cart) get bolted on below. They're no-ops until the
 *      variant markup is in place.
 *
 * Re-applies on AJAX re-renders (the drawer is replaced wholesale when
 * something fetches `?sections=cart-drawer`).
 */
(function () {
  if (window.__cartDrawerVariantInitialized) return;
  window.__cartDrawerVariantInitialized = true;

  // -------------------------------------------------------------------------
  // Visibility helpers — "active" means the drawer's wrapper isn't display:none.
  // The wrapper (.cart-drawer-experiment__control / __variant) is what
  // Intelligems toggles, not the <cart-drawer> element itself.
  // -------------------------------------------------------------------------
  function isActiveDrawer(drawer) {
    if (!drawer || !drawer.isConnected) return false;
    const wrapper = drawer.closest('.cart-drawer-experiment');
    if (!wrapper) return true;
    return window.getComputedStyle(wrapper).display !== 'none';
  }

  function findActiveDrawer(except) {
    const drawers = document.querySelectorAll('cart-drawer');
    for (const d of drawers) {
      if (d === except) continue;
      if (isActiveDrawer(d)) return d;
    }
    return null;
  }

  // -------------------------------------------------------------------------
  // Patch the .open() method on every <cart-drawer>. If a hidden drawer is
  // asked to open, forward the call to the visible one. Custom elements may
  // not be upgraded yet on first sight (Shopify's bundle defines the class
  // asynchronously) — retry on the next frame if `.open` isn't a function.
  // -------------------------------------------------------------------------
  function patchDrawer(drawer) {
    if (drawer.__variantShimPatched) return;
    if (typeof drawer.open !== 'function') {
      requestAnimationFrame(() => patchDrawer(drawer));
      return;
    }
    drawer.__variantShimPatched = true;
    const originalOpen = drawer.open;
    drawer.open = function () {
      if (isActiveDrawer(this)) {
        return originalOpen.apply(this, arguments);
      }
      const active = findActiveDrawer(this);
      if (active) {
        // active.open is either the original (unpatched) or the patched
        // version — calling either is correct because the patched version
        // sees `isActiveDrawer(this) === true` and calls originalOpen.
        return active.open.apply(active, arguments);
      }
      // No active drawer (shouldn't happen) — silently no-op.
    };
  }

  function patchAll() {
    document.querySelectorAll('cart-drawer').forEach(patchDrawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchAll);
  } else {
    patchAll();
  }

  // Watch for AJAX re-renders. The cart drawer section is replaced wholesale
  // when add/qty actions fetch `?sections=cart-drawer`, so new <cart-drawer>
  // elements appear and need patching.
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.tagName && node.tagName.toLowerCase() === 'cart-drawer') {
          patchDrawer(node);
        } else if (node.querySelectorAll) {
          node.querySelectorAll('cart-drawer').forEach(patchDrawer);
        }
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  // Raised by refreshDrawer() while it swaps drawer contents, so the
  // control-drawer observer doesn't treat our own swap as an external change.
  let suppressExternalSync = false;

  // Section Shopify renders for us. cart.js add/change accept `sections` +
  // `sections_url` and return the freshly-rendered HTML in the SAME response —
  // that HTML is atomically consistent with the change just made.
  var CART_DRAWER_SECTION = 'cart-drawer';

  // The navbar cart-count bubble lives in `#cart-icon-bubble` (header.liquid) and
  // has its own section file. We ask Shopify to render it alongside the drawer in
  // every cart mutation so the same atomic, never-stale response also carries the
  // fresh bubble — no extra (cacheable) GET, no risk of a stale count.
  var CART_BUBBLE_SECTION = 'cart-icon-bubble';

  // Replace the navbar cart-count bubble with freshly-rendered section HTML.
  // `html` is the rendered `sections/cart-icon-bubble.liquid` output, wrapped by
  // Shopify in a `.shopify-section` div whose inner content is exactly what lives
  // inside `#cart-icon-bubble`. Purely additive: if the section is missing or the
  // anchor isn't on the page, this is a no-op and the drawer is unaffected.
  function updateCartCountBubble(html) {
    if (!html) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var section = doc.querySelector('.shopify-section') || doc.body;

    // 1) Standard theme header — swap the bubble's inner HTML wholesale.
    var live = document.getElementById('cart-icon-bubble');
    if (live && section) live.innerHTML = section.innerHTML;

    // 2) Alternate product-page header (rv-product-header.liquid) uses its own
    //    markup (`[data-rv-cart] > .rv-product-header__cart-count`) and is NOT an
    //    #cart-icon-bubble, so the swap above misses it. The displayed number is
    //    the same non-upsell count, so derive it from the rendered bubble and
    //    reconcile this header's count span (create / update / remove).
    updateAltCartCount(getNonUpsellCount(doc));
  }

  // Read the non-upsell item count out of the rendered cart-icon-bubble HTML.
  // The `.cart-count-bubble` only exists when count > 0. Below 100 it has a
  // visible `<span aria-hidden="true">N</span>` — read THAT span directly. (We
  // can't scan the whole bubble's text: the screen-reader "N items" span sits
  // right after the digit span with no separator, so e.g. "1" + "1 item" reads
  // as "11".) At 100+ there's no digit span, so fall back to the localized text.
  function getNonUpsellCount(doc) {
    var bubble = doc.querySelector('.cart-count-bubble');
    if (!bubble) return 0;
    var digit = bubble.querySelector('span[aria-hidden="true"]');
    if (digit) {
      var n = parseInt(digit.textContent.trim(), 10);
      if (!isNaN(n)) return n;
    }
    var m = bubble.textContent.replace(/[,\s]/g, '').match(/\d+/);
    return m ? parseInt(m[0], 10) : 0;
  }

  // Reconcile the product-page header's cart count span to `count`. Mirrors the
  // liquid: the span only exists when count > 0. No-op on pages without this
  // header (the [data-rv-cart] anchor is absent).
  function updateAltCartCount(count) {
    document.querySelectorAll('[data-rv-cart]').forEach(function (link) {
      var span = link.querySelector('.rv-product-header__cart-count');
      if (count > 0) {
        if (!span) {
          span = document.createElement('span');
          span.className = 'rv-product-header__cart-count';
          span.setAttribute('aria-hidden', 'true');
          link.appendChild(span);
        }
        // Match the standard theme bubble: show the digit only below 100,
        // otherwise leave the styled dot empty (no number) for 100+.
        span.textContent = count < 100 ? String(count) : '';
      } else if (span) {
        span.remove();
      }
    });
  }

  // -------------------------------------------------------------------------
  // Apply freshly-rendered cart-drawer section HTML to the live drawers.
  // `html` is the rendered `sections/cart-drawer.liquid` output, which contains
  // BOTH experiment wrappers. We swap only the inner content so the
  // <cart-drawer> element (and its open state) is preserved.
  //
  // Used by BOTH paths: internal add/change handlers (which pass the HTML from
  // their mutation response — never stale) and the external control-observer
  // GET fallback below.
  // -------------------------------------------------------------------------
  function applyCartDrawerHtml(html) {
    if (!html) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');

    // `suppressExternalSync` is raised across the swaps so the control-drawer
    // observer doesn't mistake OUR swap of the control's inner HTML for an
    // external cart change and loop back into another refresh.
    suppressExternalSync = true;
    const variantOk = swapInnerContents(
      '.cart-drawer-experiment__variant .cart-drawer-v2__inner',
      doc
    );
    swapInnerContents('.cart-drawer-experiment__control .drawer__inner', doc);
    requestAnimationFrame(() => {
      suppressExternalSync = false;
    });

    if (!variantOk) {
      // Inner-swap couldn't land — fall back to replacing the whole variant
      // <cart-drawer> element (the pattern the theme uses for the control),
      // preserving its open/active state so it doesn't flicker shut.
      const freshVariant = doc.querySelector('.cart-drawer-experiment__variant cart-drawer');
      const liveVariant = document.querySelector('.cart-drawer-experiment__variant cart-drawer');
      console.warn('[CartDrawerV2] variant inner-swap skipped →', {
        responseHasVariantWrapper: !!doc.querySelector('.cart-drawer-experiment__variant'),
        responseHasVariantDrawer: !!freshVariant,
        responseHasVariantInner: !!doc.querySelector('.cart-drawer-experiment__variant .cart-drawer-v2__inner'),
        liveHasVariantDrawer: !!liveVariant,
      });
      if (freshVariant && liveVariant) {
        freshVariant.className = liveVariant.className; // keep open/active state
        liveVariant.replaceWith(freshVariant);
        patchDrawer(freshVariant); // re-apply the open() routing shim
      }
      clearLoadingStates();
    }

    document.dispatchEvent(new CustomEvent('cart-drawer-v2:refreshed'));
    return true;
  }

  // Pull the rendered cart-drawer HTML out of a cart.js (add/change) response
  // and apply it. The mutation response carries `sections['cart-drawer']` when
  // we asked for it — this is the atomic, never-stale path. If it's somehow
  // missing, fall back to the standalone GET.
  function applyDrawerFromCartResponse(result) {
    const sections = result && result.sections;
    const html = sections && sections[CART_DRAWER_SECTION];
    if (html) {
      applyCartDrawerHtml(html);
      updateCartCountBubble(sections[CART_BUBBLE_SECTION]);
    } else {
      refreshDrawer();
    }
  }

  // -------------------------------------------------------------------------
  // Standalone GET of the cart-drawer section. Used ONLY by the external
  // control-observer sync (no mutation response to read from). Internal
  // actions never use this — a separate GET can be served a STALE cached copy
  // of the cart, which is what made the drawer "revert" after a change.
  // -------------------------------------------------------------------------
  async function refreshDrawer() {
    try {
      // POST /cart/update.js with `sections` — NOT a GET of `?sections`.
      // The POST returns freshly-rendered, server-consistent section HTML that
      // is never served from a stale cache (a plain GET can be). With no
      // `updates` field the cart itself is unchanged; we only want the HTML.
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          sections: [CART_DRAWER_SECTION, CART_BUBBLE_SECTION],
          sections_url: window.location.pathname,
        }),
      });
      if (!res.ok) {
        console.error('[CartDrawerV2] /cart/update.js HTTP', res.status);
        clearLoadingStates();
        return;
      }
      const data = await res.json();
      const html = data.sections && data.sections[CART_DRAWER_SECTION];
      if (!applyCartDrawerHtml(html)) clearLoadingStates();
      if (data.sections) updateCartCountBubble(data.sections[CART_BUBBLE_SECTION]);
    } catch (err) {
      console.error('[CartDrawerV2] refreshDrawer failed:', err);
      clearLoadingStates();
    }
  }

  // Returns true if the swap actually happened (both source and target found).
  function swapInnerContents(selector, sourceDoc) {
    const source = sourceDoc.querySelector(selector);
    const target = document.querySelector(selector);
    if (source && target) {
      target.innerHTML = source.innerHTML;
      return true;
    }
    return false;
  }

  // Failsafe: if a refresh couldn't swap in fresh markup, the line item / qty
  // stepper would otherwise stay stuck in its faded `data-loading="true"` state.
  // Clear those flags so the drawer remains interactive.
  function clearLoadingStates() {
    document
      .querySelectorAll('.cart-drawer-experiment__variant [data-loading="true"]')
      .forEach((el) => {
        el.dataset.loading = 'false';
      });
  }

  // -------------------------------------------------------------------------
  // POST to /cart/add.js. `sellingPlan` is optional — pass the plan id to add
  // a subscription line item; omit for a plain one-time purchase.
  // -------------------------------------------------------------------------
  async function addToCart(variantId, quantity, sellingPlan) {
    if (!variantId) throw new Error('addToCart: missing variantId');
    const body = {
      items: [
        {
          id: Number(variantId),
          quantity: Number(quantity) || 1,
        },
      ],
      // Ask Shopify to render the cart-drawer section in this same response so
      // we get cart HTML that's atomically consistent with the add.
      sections: [CART_DRAWER_SECTION, CART_BUBBLE_SECTION],
      sections_url: window.location.pathname,
    };
    if (sellingPlan) body.items[0].selling_plan = Number(sellingPlan);

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      let detail;
      try { detail = await res.json(); } catch (e) { detail = res.statusText; }
      console.error(
        '[CartDrawerV2] /cart/add.js ' + res.status + ' →',
        detail,
        '| sent payload:',
        body
      );
      throw new Error('addToCart failed: ' + JSON.stringify(detail));
    }
    return res.json();
  }

  // -------------------------------------------------------------------------
  // Open whichever cart drawer is currently the active layout. Used after
  // refreshDrawer() to keep the drawer visible following an add-to-cart.
  // -------------------------------------------------------------------------
  function openActiveDrawer() {
    const drawer = findActiveDrawer();
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  // -------------------------------------------------------------------------
  // External cart-change sync.
  //
  // The drawer's own buttons update it from their mutation response. But cart
  // changes made ELSEWHERE — the PDP "Add to Cart" CTA, quick-add, sticky ATC,
  // upsell apps — bypass the variant drawer, so it opens stale until reload.
  //
  // We watch every cart-mutation request (fetch + XHR) and, when one made
  // OUTSIDE the drawer completes, refresh the variant via refreshDrawer()
  // (which POSTs /cart/update.js for fresh, never-cached section HTML).
  //
  // GUARDS:
  //  - `internalMutationInFlight` is raised by the drawer's own handlers, so we
  //    don't double-refresh for actions that already update from their response.
  //  - We only act when the VARIANT is the live drawer, so the control cohort
  //    behaves exactly as the theme intends and the A/B test stays clean.
  //  - We match add/change/clear but NOT update.js — refreshDrawer itself POSTs
  //    update.js, and matching it would cause an infinite refresh loop.
  // -------------------------------------------------------------------------
  function variantIsActive() {
    const active = findActiveDrawer();
    return !!(active && active.hasAttribute('data-cart-drawer-variant'));
  }

  // Raised while the drawer's own button handlers run (they update from their
  // own mutation response), so the interceptor below skips those requests.
  let internalMutationInFlight = false;

  var CART_MUTATION_RE = /\/cart\/(add|change|clear)(\.js)?($|\?)/i;

  // Spinner shown the instant an external add opens the drawer, so the user
  // gets immediate feedback instead of either an empty-cart flash or a dead
  // pause. The fresh content replaces it when refreshDrawer swaps the inner.
  var LOADING_HTML =
    '<div class="cart-drawer-v2__loading" role="status" aria-label="Updating cart">' +
    '<span class="cart-drawer-v2__spinner"></span>' +
    '</div>';

  // Open the variant drawer RIGHT AWAY with a loading spinner. Only when it's
  // currently closed (a fresh external add) — if it's already open the refresh
  // just updates it in place, no spinner needed.
  function showLoadingAndOpen() {
    var drawer = findActiveDrawer();
    if (!drawer || drawer.classList.contains('active')) return;
    var inner = drawer.querySelector('.cart-drawer-v2__inner');
    if (inner) inner.innerHTML = LOADING_HTML;
    if (typeof drawer.open === 'function') drawer.open();
  }

  var extRefreshTimer = null;
  var extWantsOpen = false;
  function scheduleExternalRefresh(opts) {
    if (opts && opts.open) extWantsOpen = true;
    clearTimeout(extRefreshTimer);
    // Fire essentially immediately — the add is already committed, so there's
    // nothing to wait for. The tiny timeout only collapses a same-tick burst
    // (e.g. an app adding two lines at once) into a single refresh.
    extRefreshTimer = setTimeout(async () => {
      await refreshDrawer(); // swaps the spinner out for the real cart content
      if (extWantsOpen) {
        extWantsOpen = false;
        openActiveDrawer(); // no-op if showLoadingAndOpen already opened it
      }
    }, 0);
  }

  function noteCartMutation(url) {
    if (!url || !CART_MUTATION_RE.test(url)) return;
    if (internalMutationInFlight) return; // our own action — already handled
    if (!variantIsActive()) return; // control cohort — leave untouched
    var isAdd = /\/cart\/add(\.js)?($|\?)/i.test(url);
    // Open immediately with a spinner so the click feels responsive; the
    // refresh below replaces the spinner with the populated cart.
    if (isAdd) showLoadingAndOpen();
    scheduleExternalRefresh({ open: isAdd });
  }

  // -------------------------------------------------------------------------
  // Piggyback `sections` onto an outgoing external /cart/add.js so Shopify
  // renders the cart-drawer HTML inside the SAME response. Returns a rewritten
  // {input, init} pair, or null if the body shape isn't safely rewritable
  // (caller then falls back to the post-add refreshDrawer round-trip).
  // -------------------------------------------------------------------------
  function injectSectionsIntoBody(body) {
    var sections = [CART_DRAWER_SECTION, CART_BUBBLE_SECTION];
    var sectionsUrl = window.location.pathname;
    if (typeof body === 'string') {
      try {
        var parsed = JSON.parse(body);
        if (parsed && typeof parsed === 'object') {
          parsed.sections = sections;
          parsed.sections_url = sectionsUrl;
          return JSON.stringify(parsed);
        }
      } catch (e) { /* not JSON — try form-encoded */ }
      try {
        var params = new URLSearchParams(body);
        sections.forEach(function (s) { params.append('sections[]', s); });
        params.set('sections_url', sectionsUrl);
        return params.toString();
      } catch (e) { return null; }
    }
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      sections.forEach(function (s) { body.append('sections[]', s); });
      body.set('sections_url', sectionsUrl);
      return body;
    }
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      sections.forEach(function (s) { body.append('sections[]', s); });
      body.set('sections_url', sectionsUrl);
      return body;
    }
    return null;
  }

  function tryPiggybackAddRequest(input, init) {
    try {
      if (typeof input === 'string' && init && init.body !== undefined && init.body !== null) {
        var newBody = injectSectionsIntoBody(init.body);
        if (newBody == null) return null;
        var newInit = {};
        for (var k in init) if (Object.prototype.hasOwnProperty.call(init, k)) newInit[k] = init[k];
        newInit.body = newBody;
        return { input: input, init: newInit };
      }
    } catch (e) { /* fall through */ }
    return null; // Request-object inputs / unknown bodies → existing fallback
  }

  // ---- fetch() interceptor ----
  var _originalFetch = window.fetch;
  if (typeof _originalFetch === 'function') {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url ? input.url : '';
      var isExternalAdd =
        /\/cart\/add(\.js)?($|\?)/i.test(url) &&
        !internalMutationInFlight &&
        variantIsActive();

      // Try to piggyback `sections` so we can read the drawer HTML straight
      // off the add response — no second /cart/update.js round-trip.
      var piggy = isExternalAdd ? tryPiggybackAddRequest(input, init) : null;
      if (piggy) {
        showLoadingAndOpen(); // immediate feedback — replaces spinner once response lands
        var result = _originalFetch.call(this, piggy.input, piggy.init);
        result.then(function (res) {
          if (!res || !res.ok) { clearLoadingStates(); return; }
          res.clone().json().then(function (data) {
            if (data && data.sections && data.sections[CART_DRAWER_SECTION]) {
              applyDrawerFromCartResponse(data);
              openActiveDrawer();
            } else {
              // Server didn't return sections (unexpected) — fall back.
              scheduleExternalRefresh({ open: true });
            }
          }).catch(function () {
            scheduleExternalRefresh({ open: true });
          });
        }).catch(function () { clearLoadingStates(); });
        return result;
      }

      // Non-add, or add we couldn't safely rewrite — original behavior.
      var result = _originalFetch.apply(this, arguments);
      try {
        if (CART_MUTATION_RE.test(url)) {
          result.then(function (res) { if (res && res.ok) noteCartMutation(url); }).catch(function () {});
        }
      } catch (e) { /* never break the original request */ }
      return result;
    };
  }

  // ---- XMLHttpRequest interceptor (jQuery-based apps / older code) ----
  var _xhrOpen = XMLHttpRequest.prototype.open;
  var _xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__cartV2Url = url;
    return _xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    var url = this.__cartV2Url || '';
    if (CART_MUTATION_RE.test(url)) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) noteCartMutation(url);
      });
    }
    return _xhrSend.apply(this, arguments);
  };

  // -------------------------------------------------------------------------
  // Phase 3 — change the quantity of an existing line item, or remove it
  // entirely (quantity: 0). Shopify's /cart/change.js re-evaluates automatic
  // discounts on every change, so refreshing the drawer afterwards picks up
  // BUY2SAVE5 activation / deactivation for free.
  // -------------------------------------------------------------------------
  // `line` is the 1-based cart line index (Shopify's `line` param), NOT the
  // line item key. This store's cart rejects key-based (`id`) change.js requests
  // with a 400 — the theme's own (working) drawer changes lines by index too.
  async function changeLineQuantity(line, quantity) {
    if (!line) throw new Error('changeLineQuantity: missing line index');
    const payload = {
      line: Number(line),
      quantity: Number(quantity),
      // Render the cart-drawer section in this same response (atomic, never
      // stale) instead of doing a separate cacheable GET afterwards.
      sections: [CART_DRAWER_SECTION, CART_BUBBLE_SECTION],
      sections_url: window.location.pathname,
    };
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      let detail;
      try { detail = await res.json(); } catch (e) { detail = res.statusText; }
      // Surface the exact reason Shopify rejected the change, plus what we sent,
      // so a 400 is diagnosable from the console instead of a generic failure.
      console.error(
        '[CartDrawerV2] /cart/change.js ' + res.status + ' →',
        detail,
        '| sent payload:',
        payload
      );
      throw new Error('changeLineQuantity failed: ' + JSON.stringify(detail));
    }
    return res.json();
  }

  // -------------------------------------------------------------------------
  // Click delegation for variant-only buttons.
  //   [data-cart-v2-add]         — add the button's variant id to cart.
  //   [data-cart-v2-qty-change]  — increment/decrement the line quantity.
  //   [data-cart-v2-remove]      — remove the line entirely.
  // -------------------------------------------------------------------------
  document.addEventListener('click', async (evt) => {
    // ---- Add to cart -------------------------------------------------------
    const addBtn = evt.target.closest('[data-cart-v2-add]');
    if (addBtn) {
      evt.preventDefault();
      if (addBtn.disabled || addBtn.dataset.loading === 'true') return;

      const variantId = addBtn.dataset.variantId;
      if (!variantId) {
        console.warn('[CartDrawerV2] add button missing data-variant-id', addBtn);
        return;
      }

      addBtn.dataset.loading = 'true';
      internalMutationInFlight = true;
      const originalText = addBtn.textContent;
      addBtn.textContent = 'Adding...';

      try {
        const result = await addToCart(variantId, addBtn.dataset.quantity || 1, addBtn.dataset.sellingPlan);
        applyDrawerFromCartResponse(result);
        openActiveDrawer();
      } catch (err) {
        console.error('[CartDrawerV2] add-to-cart failed:', err);
        addBtn.textContent = originalText;
      } finally {
        internalMutationInFlight = false;
        if (addBtn.isConnected) addBtn.dataset.loading = 'false';
      }
      return;
    }

    // ---- Quantity stepper (+/-) -------------------------------------------
    const qtyBtn = evt.target.closest('[data-cart-v2-qty-change]');
    if (qtyBtn) {
      evt.preventDefault();
      const stepper = qtyBtn.closest('[data-cart-v2-qty-line]');
      if (!stepper || stepper.dataset.loading === 'true') return;
      const lineIndex = stepper.dataset.cartV2LineIndex;
      const item = stepper.closest('[data-cart-v2-line]');
      const current = item ? parseInt(item.dataset.cartV2Quantity, 10) || 0 : 0;
      const delta = parseInt(qtyBtn.dataset.cartV2QtyChange, 10) || 0;
      const next = Math.max(0, current + delta);
      if (next === current) return;

      stepper.dataset.loading = 'true';
      internalMutationInFlight = true;
      try {
        const result = await changeLineQuantity(lineIndex, next);
        applyDrawerFromCartResponse(result);
        // Re-open the drawer — replacing the <cart-drawer> element on refresh
        // resets it to its default closed state, so calling .open() puts it
        // back to the state the user expects (they were interacting with it).
        openActiveDrawer();
      } catch (err) {
        console.error('[CartDrawerV2] qty change failed:', err);
        if (stepper.isConnected) stepper.dataset.loading = 'false';
      } finally {
        internalMutationInFlight = false;
      }
      // No stepper re-enable in success — the element is replaced on refresh.
      return;
    }

    // ---- Upgrade line to subscription (Phase 4) ---------------------------
    const upgradeBtn = evt.target.closest('[data-cart-v2-upgrade]');
    if (upgradeBtn) {
      evt.preventDefault();
      if (upgradeBtn.dataset.loading === 'true') return;

      const lineIndex = upgradeBtn.dataset.cartV2LineIndex;
      const planId  = upgradeBtn.dataset.cartV2PlanId;
      const qty     = parseInt(upgradeBtn.dataset.cartV2Quantity, 10) || 1;
      if (!lineIndex || !planId) {
        console.warn('[CartDrawerV2] upgrade button missing line index or plan id', upgradeBtn);
        return;
      }

      upgradeBtn.dataset.loading = 'true';
      internalMutationInFlight = true;
      const originalText = upgradeBtn.textContent;
      upgradeBtn.textContent = 'Upgrading...';

      try {
        // /cart/change.js accepts `selling_plan` to swap a line in place —
        // Shopify converts the existing line to the subscription version.
        // Identify the line by index (not key) — see changeLineQuantity().
        const payload = {
          line: Number(lineIndex),
          quantity: qty,
          selling_plan: Number(planId),
          sections: [CART_DRAWER_SECTION, CART_BUBBLE_SECTION],
          sections_url: window.location.pathname,
        };
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          let detail;
          try { detail = await res.json(); } catch (e) { detail = res.statusText; }
          console.error(
            '[CartDrawerV2] /cart/change.js (upgrade) ' + res.status + ' →',
            detail,
            '| sent payload:',
            payload
          );
          throw new Error('upgrade failed: ' + JSON.stringify(detail));
        }
        const result = await res.json();
        applyDrawerFromCartResponse(result);
        openActiveDrawer();
      } catch (err) {
        console.error('[CartDrawerV2] upgrade failed:', err);
        upgradeBtn.textContent = originalText;
        if (upgradeBtn.isConnected) upgradeBtn.dataset.loading = 'false';
      } finally {
        internalMutationInFlight = false;
      }
      return;
    }

    // ---- Remove line ------------------------------------------------------
    const removeBtn = evt.target.closest('[data-cart-v2-remove]');
    if (removeBtn) {
      evt.preventDefault();
      if (removeBtn.dataset.loading === 'true') return;
      const lineIndex = removeBtn.dataset.cartV2LineIndex;
      if (!lineIndex) {
        console.warn('[CartDrawerV2] remove button missing data-cart-v2-line-index', removeBtn);
        return;
      }
      removeBtn.dataset.loading = 'true';
      internalMutationInFlight = true;
      try {
        const result = await changeLineQuantity(lineIndex, 0);
        applyDrawerFromCartResponse(result);
        openActiveDrawer();
      } catch (err) {
        console.error('[CartDrawerV2] remove failed:', err);
        if (removeBtn.isConnected) removeBtn.dataset.loading = 'false';
      } finally {
        internalMutationInFlight = false;
      }
      return;
    }
  });

  // -------------------------------------------------------------------------
  // Upsell swiper — desktop drag-to-scroll
  //
  // The swiper track has `overflow-x: auto` so it scrolls natively, but
  // desktop mice can't pan horizontally (only trackpads can two-finger
  // pan; vertical-only wheel mice need Shift+wheel, which users don't
  // discover). Touch devices already work via CSS `touch-action: pan-x`.
  // We add click-drag-to-scroll for mouse users so the swiper has a
  // discoverable interaction model on desktop.
  //
  // A click that didn't actually drag (delta < threshold) is allowed to
  // propagate to the underlying anchor/button. A real drag suppresses
  // the synthetic click via a capture-phase listener on the next tick.
  // -------------------------------------------------------------------------
  const SWIPER_TRACK_SELECTOR = '.cart-drawer-v2__upsell-swiper-track';
  const DRAG_CLICK_THRESHOLD_PX = 5;

  function initSwiperDrag(track) {
    if (track.__swiperDragInit) return;
    track.__swiperDragInit = true;

    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let dragged = false;

    track.addEventListener('mousedown', (e) => {
      // Only primary button. Don't hijack right-clicks or middle-clicks.
      if (e.button !== 0) return;
      isDown = true;
      dragged = false;
      startX = e.pageX;
      startScrollLeft = track.scrollLeft;
      track.classList.add('is-dragging');
    });

    // mouseleave on the track itself ends the drag — without this, releasing
    // outside the track leaves `isDown` true and the next mousedown elsewhere
    // can resume scrolling unexpectedly.
    const endDrag = () => {
      if (!isDown) return;
      isDown = false;
      track.classList.remove('is-dragging');
    };
    track.addEventListener('mouseleave', endDrag);
    track.addEventListener('mouseup', endDrag);

    track.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      const dx = e.pageX - startX;
      if (Math.abs(dx) > DRAG_CLICK_THRESHOLD_PX) {
        dragged = true;
        // Prevent text selection / native image drag while dragging.
        e.preventDefault();
      }
      track.scrollLeft = startScrollLeft - dx;
    });

    // After a real drag, the browser dispatches a synthetic click on the
    // element under the pointer. We swallow it once, in the capture phase,
    // so the user doesn't accidentally navigate to a product page (or
    // trigger ADD TO CART) just by releasing a drag.
    track.addEventListener(
      'click',
      (e) => {
        if (dragged) {
          e.preventDefault();
          e.stopPropagation();
          dragged = false;
        }
      },
      true,
    );
  }

  // ---- TEST: arrow + bullet navigation (revertable block) ---------------
  // Wires the prev/next arrows and bullet dots in the swiper header/footer
  // to the track's scroll position. Click arrow → scroll by one slide.
  // Click dot N → scroll so slide N is at the start. Both fall through
  // to `scrollTo({behavior: 'smooth'})` so they play nicely with the
  // drag-to-scroll handler above.
  function initSwiperNav(track) {
    if (track.__swiperNavInit) return;
    track.__swiperNavInit = true;

    const section = track.closest('.cart-drawer-v2__upsell-swiper');
    if (!section) return;

    const prevBtn = section.querySelector('[data-cart-v2-swiper-nav="prev"]');
    const nextBtn = section.querySelector('[data-cart-v2-swiper-nav="next"]');
    const dots    = section.querySelectorAll('[data-cart-v2-swiper-dot]');

    function slideStep() {
      // Single slide width plus the flex gap. Read live so this stays
      // correct after resize / orientation change.
      const slide = track.querySelector('.cart-drawer-v2__upsell-swiper-slide');
      if (!slide) return 0;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    function updateUi() {
      const max = track.scrollWidth - track.clientWidth;
      const pos = track.scrollLeft;
      if (prevBtn) prevBtn.disabled = pos <= 1;
      if (nextBtn) nextBtn.disabled = pos >= max - 1;
      if (dots.length) {
        // When we're at the right edge of the scroll, the last slide is
        // fully visible but the track can't scroll further right to make
        // it the "start" — so `pos / step` rounds down to a lower index
        // and the last dot would never light up. Force the last dot
        // active whenever we're at (or past) the scroll maximum.
        let activeIndex;
        if (pos >= max - 1) {
          activeIndex = dots.length - 1;
        } else {
          const step = slideStep();
          activeIndex = step > 0 ? Math.round(pos / step) : 0;
        }
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === activeIndex);
        });
      }
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        track.scrollBy({ left: -slideStep(), behavior: 'smooth' });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        track.scrollBy({ left: slideStep(), behavior: 'smooth' });
      });
    }
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.index, 10) || 0;
        // Clamp to the scroll max so clicking the last dot lands at the
        // right edge (the last slide can't reach the "start" position).
        const max = track.scrollWidth - track.clientWidth;
        const target = Math.min(idx * slideStep(), max);
        track.scrollTo({ left: target, behavior: 'smooth' });
      });
    });

    track.addEventListener('scroll', updateUi, { passive: true });
    // Initial paint + after a frame so layout is settled (slide widths
    // are accurate even when the swiper appears inside an animated
    // drawer that hasn't reached its final size yet).
    updateUi();
    requestAnimationFrame(updateUi);
  }
  // ---- end TEST block ---------------------------------------------------

  function initAllSwipers(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(SWIPER_TRACK_SELECTOR).forEach((track) => {
      initSwiperDrag(track);
      initSwiperNav(track); // TEST: arrow + bullet nav
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllSwipers());
  } else {
    initAllSwipers();
  }

  // Re-initialize after AJAX cart re-renders (the drawer's inner HTML is
  // swapped on every cart mutation, so the track elements are brand new).
  document.addEventListener('cart-drawer-v2:refreshed', () => initAllSwipers());

  // Late-arriving recommendations: the <product-recommendations> element
  // fetches asynchronously and replaces its own inner HTML after intersect.
  // Watch for the swiper track appearing inside any recommendations element
  // and initialize it as soon as it's there.
  const swiperObserver = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches(SWIPER_TRACK_SELECTOR)) {
          initSwiperDrag(node);
        } else if (node.querySelectorAll) {
          initAllSwipers(node);
        }
      }
    }
  });
  swiperObserver.observe(document.documentElement, { childList: true, subtree: true });

  // -------------------------------------------------------------------------
  // Public namespace for phase 1+ helpers. Kept on window for cross-file
  // access and for debugging from the console.
  // -------------------------------------------------------------------------
  window.CartDrawerV2 = window.CartDrawerV2 || {};
  Object.assign(window.CartDrawerV2, {
    isActiveDrawer,
    findActiveDrawer,
    refreshDrawer,
    addToCart,
    openActiveDrawer,
    initAllSwipers,
  });
})();
